<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/point_ledgers/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>

  <div class="ml-12">
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :row-link="
          (item) => {
            const driverId = item.driver?.id ?? '-';
            return {
              name: 'driver-points',
              params: { driverId },
            };
          }
        "
        :columns="columns"
        :visible-columns="visibleColumns"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { useProvider } from '@utils/table/hooks/provider';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {
  HydraCollection,
  PointLedger,
  PointReward,
} from '@/entities/Entities';
import { useQueryRef } from '@hooks/url';
import { useTableFields } from '@hooks/table-configuration';
import { format } from 'date-fns';

interface Props {
  modelValue: PointReward|null;
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.pointRewardDetails', {
    pointReward: props.modelValue?.name,
  });
});


const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'pointReward.id': props.modelValue?.id,
}));

const totalFilter = computed(() => {
  const filters: any = { ...filter.value, ...fixedFilter.value };
  if (filters['amount'] === true) {
    filters['amount'] = { from: 1 };
  }
  if (filters['amount'] === false) {
    filters['amount'] = { to: 0 };
  }
  return filters;
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns = computed<ColumnDefinition[]>(() => ([
  {
    key: 'id',
    title: t('id'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    computed: (item: PointLedger) => item.id ?? '-',
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'timestamp',
    title: t('pointLedger.timestamp'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    visible: true,
    filter: {
      type: 'datetime',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pointLedger.timestamp'),
      },
    },
    sortable: true,
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    computed: (item: PointLedger) => item.driver?.user?.firstName ?? '-',
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    computed: (item: PointLedger) => item.driver?.user?.lastName ?? '-',
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'amount',
    title: t('pointLedger.amount'),
    group: t('pages.pointRewardList.fieldGroups.general'),
    computed: (item: PointLedger) => item.amount ?? '-',
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.pointRewardList.filterGroups.general'),
      options: {
        label: t('pointLedger.amount'),
      },
    },
  },
]));

const visibleColumns = useTableFields(columns);

const blockRequest = computed(() => props.modelValue === null);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<PointLedger>>('/point_ledgers/drivers', providerParams, {
  disabled: blockRequest,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  unref(columns).flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
